// api.js
import axios from 'axios';

const API_BASE_URL = 'https://demo-api.currykeeper.com';

// Add to api.js
export const authApi = {
  login: async (username, password) => {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);
    
    try {
      const response = await api.post('/auth/token', formData.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
};

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const checkMaterialName = async (name) => {
  try {
    const response = await api.get(`/inventory/materials/check-name/${encodeURIComponent(name)}`);
    return response.data;
  } catch (error) {
    console.error('Error checking material name:', error);
    throw error;
  }
};

export const inventoryApi = {
  checkMaterialName,
  getSpellingSuggestions: async (word) => {
    try {
      const response = await api.get(`/inventory/materials/similar-names/${encodeURIComponent(word)}`);
      return response.data;
    } catch (error) {
      console.error('Error checking similar names:', error);
      throw error.response?.data || error.message;
    }
  },
  
  createMaterial: async (formData) => {
    try {
      const response = await api.post('/inventory/materials/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateMaterial: async (materialId, formData, removeImage = false) => {
    try {
      if (removeImage) {
        formData.append('remove_image', 'true');
      }
      const response = await api.put(`/inventory/materials/${materialId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getMaterials: async (category = null) => {
    try {
      const params = {};
      if (category && category !== 'All') {
        params.category = category;
      }
      const response = await api.get('/inventory/materials/', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getMaterial: async (materialId) => {
    try {
      const response = await api.get(`/inventory/materials/${materialId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteMaterial: async (materialId) => {
    try {
      await api.delete(`/inventory/materials/${materialId}`);
      return true;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

 getAlerts: async () => {
    try {
      const response = await api.get('/inventory/alerts/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  uploadExcelMaterials: async (formData, onUploadProgress) => {
    try {
      const response = await api.post('/inventory/materials/upload-excel/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress
      });
      return response;
    } catch (error) {
      console.error('API Error:', error);
      throw error.response?.data || { detail: error.message };
    }
  },
};

// Add this new function to your api.js
export const getSpellingSuggestions = async (word) => {
  try {
    const response = await fetch(`https://api.datamuse.com/words?sp=${word}&max=3`);
    const data = await response.json();
    return data.map(item => item.word);
  } catch (error) {
    console.error('Error getting spelling suggestions:', error);
    return [];
  }
};


api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Recipe API functions
// Update the getInventoryWarnings function
export const getInventoryWarnings = async () => {
  try {
    const response = await api.get('/inventory/warnings');
    return response.data;
  } catch (error) {
    console.error('Error fetching inventory warnings:', error);
    // Return empty warnings if endpoint fails
    return {
      lowStock: [],
      expiringSoon: [],
      expired: []
    };
  }
};

// Update getRecipes to handle errors better
export const getRecipes = async () => {
  try {
    const response = await api.get('/recipes/');
    return response.data;
  } catch (error) {
    console.error('Error fetching recipes:', error);
    // Return empty array if endpoint fails
    return [];
  }
};

export const getRecipe = async (id) => {
  try {
    const response = await api.get(`/recipes/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching recipe:', error);
    throw error;
  }
};

export const createRecipe = async (formData) => {
  try {
    const response = await api.post('/recipes/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error creating recipe:', error);
    throw error.response?.data || error;
  }
};

export const updateRecipe = async (id, formData) => {
  try {
    const response = await api.put(`/recipes/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating recipe:', error);
    throw error.response?.data || error;
  }
};

export const deleteRecipe = async (id) => {
  try {
    const response = await api.delete(`/recipes/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting recipe:', error);
    throw error;
  }
};

export const bulkUploadRecipes = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/recipes/bulk-upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk uploading recipes:', error);
    throw error;
  }
};

// src/api.js
// Add these exports at the bottom of the file
export const getMaterials = async (category = null) => {
  try {
    const response = await api.get('/inventory/materials/', {
      params: { category }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching materials:', error);
    return [];
  }
};

export const getAlerts = async () => {
  try {
    const response = await api.get('/inventory/alerts/');
    return response.data;
  } catch (error) {
    console.error('Error fetching alerts:', error);
    return [];
  }
};

export const getReports = async () => {
  try {
    const response = await api.get('/reports/');
    return response.data;
  } catch (error) {
    console.error('Error fetching reports:', error);
    return null;
  }
};

export const getInventoryTrend = async (days = 30) => {
  try {
    const response = await api.get(`/reports/inventory-trend?days=${days}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching inventory trend:', error);
    return { labels: [], data: [] };
  }
};

